import type { EnvKind } from '@hangar/shared';
import { FormField, fieldA11y } from '@/components/FormField';
import { Tag } from '@/components/badges';
import { Input } from '@/components/ui/input';
import { OptionSelect } from '@/components/OptionSelect';
import { ENV_KINDS, ENV_KIND_LABEL } from './environmentHelpers';
import { digitsOnly, withName, withSlug } from './environmentFormModel';
import type { EnvironmentFormApi } from './useEnvironmentForm';

type FormProp = { form: EnvironmentFormApi };

function ColorPicker({ form }: FormProp) {
  const { view, edit } = form;
  return (
    <label className="relative flex size-9 shrink-0 cursor-pointer items-center justify-center rounded-lg border bg-background max-sm:size-11" title="Environment color">
      <span aria-hidden className="size-5 rounded-md [background:var(--pill)]" />
      <span className="sr-only">Environment color</span>
      <input
        type="color"
        data-testid="env-form-color"
        value={view.shownColor.toLowerCase()}
        onChange={(e) => edit((v) => ({ ...v, color: e.target.value.toUpperCase() }))}
        className="absolute inset-0 cursor-pointer opacity-0"
      />
    </label>
  );
}

/** Name (which suggests the slug until the slug is typed by hand) with the color swatch. */
export function NameField({ form }: FormProp) {
  const { values, edit, errorFor } = form;
  return (
    <FormField id="env-form-name" label="Environment name" required error={errorFor('name')}>
      <div className="flex items-center gap-2">
        <ColorPicker form={form} />
        <Input
          data-testid="env-form-name"
          value={values.name}
          maxLength={120}
          autoComplete="off"
          placeholder="e.g. Testing"
          onChange={(e) => edit((v) => withName(v, e.target.value), 'name')}
          {...fieldA11y('env-form-name', errorFor('name'))}
        />
      </div>
    </FormField>
  );
}

export function SlugField({ form }: FormProp) {
  const { env, values, edit, errorFor } = form;
  return (
    <FormField id="env-form-slug" label="Slug" required hint={env ? 'Builds, requirements and pipelines use it; fixed once one does' : 'hangar build --env <slug>'} error={errorFor('slug')}>
      <Input
        data-testid="env-form-slug"
        value={values.slug}
        maxLength={63}
        autoComplete="off"
        spellCheck={false}
        placeholder="testing"
        className="font-mono sm:text-[13px]"
        onChange={(e) => edit((v) => withSlug(v, e.target.value), 'slug')}
        {...fieldA11y('env-form-slug', errorFor('slug'), 'env-form-slug-hint')}
      />
    </FormField>
  );
}

export function OrderField({ form }: FormProp) {
  const { values, edit, errorFor } = form;
  return (
    <FormField id="env-form-sortOrder" label="Execution order" hint="Lower comes first in promotion" error={errorFor('sortOrder')}>
      <Input data-testid="env-form-order" inputMode="numeric" value={values.order} onChange={(e) => edit((v) => ({ ...v, order: digitsOnly(e.target.value) }))} className="tabular font-mono sm:text-[13px]" {...fieldA11y('env-form-sortOrder', errorFor('sortOrder'), 'env-form-sortOrder-hint')} />
    </FormField>
  );
}

/** Kind: a select for a new environment, read-only text for an existing one (it never changes). */
export function KindField({ form }: FormProp) {
  const { env, values, edit } = form;
  if (env) {
    return (
      <div className="flex min-w-0 flex-col gap-1.5">
        <span className="text-[13px] font-medium text-foreground">Kind</span>
        <span className="flex h-9 items-center gap-2 text-[13px] text-muted-foreground">
          <Tag>{env.kind}</Tag>
          fixed once created
        </span>
      </div>
    );
  }
  return (
    <FormField id="env-form-kind" label="Kind" required hint="What the environment is for; it picks the default color">
      <OptionSelect
        id="env-form-kind"
        data-testid="env-form-kind"
        value={values.kind}
        onValueChange={(kind) => edit((v) => ({ ...v, kind: kind as EnvKind }))}
        aria-describedby="env-form-kind-hint"
        options={ENV_KINDS.map((k) => ({ value: k, label: ENV_KIND_LABEL[k] }))}
      />
    </FormField>
  );
}
